import React, { useMemo } from "react";
import MuiAlert, { AlertColor } from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";

import { Text } from "merchant-portal-common/Text";
import { SxProps } from "merchant-portal-theme/ThemeTypes";

export enum AlertStatus {
	Info = "INFO",
	Error = "ERROR",
	Success = "SUCCESS",
}

export type AlertItemMessage = string | (string | string[])[];

export interface AlertProps {
	status: AlertStatus;
	message: AlertItemMessage;
	onClose?: () => void;
	sx?: SxProps;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
	({ status, message, onClose, sx }, ref) => {
		const alertTitle = useMemo(() => {
			switch (status) {
				case AlertStatus.Error:
					return "Error";
				case AlertStatus.Info:
					return "Info";
				case AlertStatus.Success:
					return "Success";
				default:
					return "Info";
			}
		}, [status]);

		const renderMessage = () => {
			if (Array.isArray(message)) {
				return message.map((m) => {
					let keyId: string;
					if (Array.isArray(m)) {
						keyId = m.join(",");
					} else {
						keyId = m;
					}
					return (
						<Text key={keyId} type="body" bold>
							{m}
						</Text>
					);
				});
			}
			return <Text type="body">{message}</Text>;
		};

		return (
			<MuiAlert
				ref={ref}
				data-testid="alert-wrapper"
				variant="outlined"
				sx={{
					bgcolor: "background.paper",
					borderRadius: "20px",
					...sx,
				}}
				onClose={onClose || undefined}
				severity={status.toLowerCase() as AlertColor}
			>
				<AlertTitle sx={{ fontWeight: 700 }}>{alertTitle}</AlertTitle>
				<Box width={275}>{renderMessage()}</Box>
			</MuiAlert>
		);
	},
);
